<script>
import {
  fetchOptionsets,
  fetchOptionset,
  createOptionset,
  updateOptionset,
  deleteOptionset,
  fetchOptions,
  fetchOption,
  createOption,
  updateOption,
  deleteOption,
} from '@apis/admin/option'
import LangInput from '@components/admin/lang-input'
export default {
  components: { LangInput },
  data() {
    return {
      optionsets: [],
      options: [],
      s_optionset_seq: null,
      params: {},
      show_dialog: false,
      loading: false,
    }
  },
  watch: {
    '$attrs.value'(to) {
      this.s_optionset_seq = to
    },
  },
  created() {
    this.s_optionset_seq = this.$attrs.value
    this.init()
  },
  methods: {
    init() {
      Promise.all([this.loadOptionsets()]).catch((e) =>
        this.$toaster.error(e.message)
      )
    },
    async loadOptionsets() {
      const { data } = await fetchOptionsets()

      this.optionsets = data
    },
    async loadOptions() {
      if (!this.params.seq) return

      const { data } = await fetchOptions(this.params.seq)

      this.options = data
    },
    resetParams() {
      this.params = {
        seq: null,
        title: '',
        description: '',
      }
      this.options = []
    },
    handleCreateOptionset() {
      this.resetParams()
      this.show_dialog = true
    },
    handleUpdateOptionset(row) {
      this.params = row
      this.loadOptions().catch((e) => this.$toasted.error(e.message))

      this.show_dialog = true
    },
    handleCreateOption() {
      this.options.push({
        seq: null,
        list_order: null,
        name: '',
        title: '',
      })
    },
    handleSubmitOptionset() {
      this.loading = true
      const params = this.params
      ;(params.seq ? updateOptionset : createOptionset)(params)
        .then(({ seq }) => fetchOptionset(seq))
        .then(({ optionset_info }) => {
          this.params = { ...optionset_info }
          return Promise.all([this.loadOptionsets(), this.loadOptions()])
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async handleDeleteOptionset(seq) {
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.msg_optionset_delete'),
            this.$t('admin.modal_confirm'),
            { type: 'warning' }
          ).catch(() => false))
        )
          return

        await deleteOptionset(seq)
        await this.loadOptionsets()
      } catch (e) {
        this.$toasted.error(e.message)
      }
    },
    handleSubmitOption(row) {
      const idx = this.options.indexOf(row)

      ;(row.seq ? updateOption : createOption)({
        ...row,
        optionset_seq: this.params.seq,
      })
        .then(({ seq }) => fetchOption(this.params.seq, seq))
        .then(({ option_info }) => {
          this.$set(this.options, idx, option_info)
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async handleDeleteOption(row) {
      const idx = this.options.indexOf(row)

      this.loading = true
      try {
        if (row.seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deleteOption(this.params.seq, row.seq)
        }

        this.options.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<template>
  <div :class="$style.tLeft">
    <ElSelect
      v-model="s_optionset_seq"
      style="width: 100%;"
      filterable
      :placeholder="$t('admin.no_optionset')"
      @change="$emit('input', s_optionset_seq)"
    >
      <ElOption
        :key="0"
        :value="0"
        style="cursor: auto;"
        :label="$t('admin.yes_optionset')"
        disabled
      >
        <span :v-html="$t('admin.optionset_add')"></span>
        <div :class="$style.fr">
          <ElButtonGroup>
            <ElButton
              size="mini"
              icon="el-icon-plus"
              @click.stop="handleCreateOptionset"
            ></ElButton>
          </ElButtonGroup>
        </div>
      </ElOption>
      <ElOption
        v-for="optionset in optionsets"
        :key="optionset.seq"
        :label="optionset.title"
        :value="optionset.seq"
      >
        {{ optionset.title }}
        <div :class="$style.fr">
          <ElButtonGroup>
            <ElButton
              size="mini"
              icon="el-icon-edit"
              @click.stop="handleUpdateOptionset(optionset)"
            ></ElButton>
            <ElButton
              size="mini"
              type="danger"
              icon="el-icon-delete"
              @click.stop="handleDeleteOptionset(optionset.seq)"
            ></ElButton>
          </ElButtonGroup>
        </div>
      </ElOption>
    </ElSelect>
    <ElDialog
      v-loading="loading"
      :visible.sync="show_dialog"
      :title="$t('admin.optionset_manage')"
    >
      <ElForm :model="params" label-width="80px">
        <ElFormItem :label="$t('admin.title')">
          <ElInput
            v-model="params.title"
            :placeholder="$t('admin.title_admin_place')"
          ></ElInput>
        </ElFormItem>
        <ElFormItem :label="$t('admin.description')">
          <ElInput
            v-model="params.description"
            :placeholder="$t('admin.description_admin_place')"
            type="textarea"
          ></ElInput>
        </ElFormItem>
        <ElFormItem>
          <ElButton type="primary" @click="handleSubmitOptionset">{{
            params.seq ? $t('admin.save') : $t('admin.enroll')
          }}</ElButton>
        </ElFormItem>
      </ElForm>
      <h3 :class="$style.xeAdmin">
        {{ $t('admin.optionset') }}
      </h3>
      <ElTable v-if="params.seq" style="width: 100%;" :data="options">
        <ElTableColumn :label="$t('admin.form_info_list_order')">
          <template slot-scope="{ row }">
            <ElInput v-model="row.list_order"></ElInput>
          </template>
        </ElTableColumn>
        <ElTableColumn :label="$t('admin.optionset_name')">
          <template slot-scope="{ row }">
            <ElInput v-model="row.name"></ElInput>
          </template>
        </ElTableColumn>
        <ElTableColumn :class="$style.wide" :label="$t('admin.title')">
          <template slot-scope="{ row }">
            <LangInput v-model="row.title"></LangInput>
          </template>
        </ElTableColumn>
        <ElTableColumn align="right">
          <template slot="header">
            <ElButton size="mini" type="primary" @click="handleCreateOption">{{
              $t('admin.add')
            }}</ElButton>
          </template>
          <template slot-scope="{ row }">
            <ElButtonGroup>
              <ElButton
                size="mini"
                icon="el-icon-check"
                @click="handleSubmitOption(row)"
                >{{ row.seq ? $t('admin.save') : $t('admin.enroll') }}</ElButton
              >
              <ElButton
                size="mini"
                type="danger"
                icon="el-icon-delete"
                @click="handleDeleteOption(row)"
                >{{ $t('admin.delete') }}</ElButton
              >
            </ElButtonGroup>
          </template>
        </ElTableColumn>
      </ElTable>
    </ElDialog>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
